import React, { useState } from 'react';
import { Calendar, Send } from 'lucide-react';
import API from '../../api'; // Your axios instance
import { addActivity } from '../../utils/activityService';

const LeaveApplication = ({ user, onLeaveApplied }) => {
  const [formData, setFormData] = useState({
    leave_type: 'vacation',
    start_date: '',
    end_date: '',
    reason: '',
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate dates before sending (optional frontend validation)
    if (new Date(formData.start_date) > new Date(formData.end_date)) {
      alert('Start date cannot be after end date');
      return;
    }

    setIsSubmitting(true);

    try {
      // Prepare payload exactly matching backend expected fields
      const payload = {
        leave_type: formData.leave_type,
        start_date: formData.start_date,
        end_date: formData.end_date,
        reason: formData.reason,
      };

      // POST leave application
      const { data } = await API.post('/leaves', payload);

      
  // 👇 Activity Logging
  const message = `${user.full_name} applied for ${formData.leave_type} leave from ${formData.start_date} to ${formData.end_date}`;
  await addActivity(user.salesteam_id || user.id, message, 'Leave');


      alert(data.message || 'Leave application submitted successfully!');
      setFormData({
        leave_type: 'vacation',
        start_date: '',
        end_date: '',
        reason: '',
      });
      onLeaveApplied?.();
    } catch (error) {
      console.error('Leave application error:', error.response || error.message);
      alert(
        error.response?.data?.error || 
        error.response?.data?.errors?.map(e => e.msg).join(', ') || 
        error.message || 
        'Failed to submit leave application.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-lg p-6">
      <div className="flex items-center space-x-3 mb-6">
        <div className="w-10 h-10 bg-green-600 rounded-lg flex items-center justify-center">
          <Calendar className="w-5 h-5 text-white" />
        </div>
        <h2 className="text-xl font-semibold text-gray-900">Apply for Leave</h2>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Leave Type
            </label>
            <select
              name="leave_type"
              value={formData.leave_type}
              onChange={handleInputChange}
              required
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
            >
              <option value="vacation">Vacation</option>
              <option value="sick">Sick Leave</option>
              <option value="personal">Personal</option>
              <option value="emergency">Emergency</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Duration
            </label>
            <div className="text-sm text-gray-600">
              {formData.start_date && formData.end_date && (
                <>
                  {Math.ceil(
                    (new Date(formData.end_date).getTime() -
                      new Date(formData.start_date).getTime()) /
                      (1000 * 60 * 60 * 24)
                  ) + 1} days
                </>
              )}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Start Date
            </label>
            <input
              type="date"
              name="start_date"
              value={formData.start_date}
              onChange={handleInputChange}
              required
              min={new Date().toISOString().split('T')[0]}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              End Date
            </label>
            <input
              type="date"
              name="end_date"
              value={formData.end_date}
              onChange={handleInputChange}
              required
              min={formData.start_date || new Date().toISOString().split('T')[0]}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Reason for Leave
          </label>
          <textarea
            name="reason"
            value={formData.reason}
            onChange={handleInputChange}
            required
            rows={4}
            placeholder="Please provide a detailed reason for your leave application..."
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent resize-none"
          />
        </div>

        <div className="flex justify-end">
          <button
            type="submit"
            disabled={isSubmitting}
            className="px-6 py-3 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center space-x-2"
          >
            <Send className="w-5 h-5" />
            <span>{isSubmitting ? 'Submitting...' : 'Submit Application'}</span>
          </button>
        </div>
      </form>
    </div>
  );
};

export default LeaveApplication;
